import { PaperTooltip } from '@/components/PaperTooltip';
import { useEnumHelpers } from '@/queries/useEnumHelpers';
import { SubprojectBuildingDetails } from '@/stores/SubprojectStore';
import {
  Box,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { lighten } from '@mui/system';
import React from 'react';

interface BuildingUsagesProps {
  subproject?: SubprojectBuildingDetails;
  onUpdate: React.Dispatch<
    React.SetStateAction<SubprojectBuildingDetails | undefined>
  >;
  readOnly?: boolean;
}

const BUILDING_IMAGE_OVERRIDES: Record<string, string> = {
  'corrections': 'waste',
  'police/fire': 'policefire',
};

const buildingImageSrc = (name: string) => {
  const key = name.toLowerCase();
  const file = BUILDING_IMAGE_OVERRIDES[key] ?? key;
  return `/img/buildings/${file}.png`;
};

export const BuildingUsage: React.FC<BuildingUsagesProps> = ({
  subproject,
  onUpdate,
  readOnly,
}) => {
  // Changed from useProjectEnums to useEnumHelpers
  const { buildingUsages } = useEnumHelpers();
  const theme = useTheme();

  // Ensure buildingUsages from enums is always an array
  const usagesArray = Array.isArray(buildingUsages) ? buildingUsages : [];

  // Fallback for subproject.buildingUsages: if it's undefined, default to an empty array.
  const currentBuildingUsages = subproject?.buildingUsages ?? [];

  return (
    <React.Fragment>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
        }}
      >
        <Typography
          variant={'h5'}
          sx={{ fontSize: 16, letterSpacing: 8, textTransform: 'uppercase' }}
        >
          What type of building?
        </Typography>
        <Typography
          variant={'caption'}
          sx={{
            m: 2,
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: 4,
            textTransform: 'uppercase',
          }}
        >
          Select all that apply
        </Typography>
      </Box>
      <Box
        pt={4}
        pb={2}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          viewTransitionName: 'ease-in',
        }}
      >
        {usagesArray.map((bu) => (
          <PaperTooltip
            key={bu.name}
            disableInteractive
            placement={'right'}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -2],
                    },
                  },
                ],
              },
            }}
            title={
              <Stack spacing={2} fontSize={'small'}>
                <Typography fontSize={'inherit'} variant='body2'>
                  <Typography
                    component='span'
                    textTransform={'capitalize'}
                    fontWeight={500}
                    variant={'inherit'}
                  >
                    {bu.name}{' '}
                  </Typography>
                  may include:
                </Typography>
                <List sx={{ p: 0 }}>
                  {bu.description.map((description, idx) => (
                    <ListItem sx={{ py: 0 }} disableGutters key={idx}>
                      <Typography fontSize={'inherit'}>
                        - {description}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Stack>
            }
          >
            <Stack
              m={1.5}
              spacing={1}
              key={bu.name}
              className={
                currentBuildingUsages.includes(bu.name) ? 'active' : undefined
              }
              sx={{
                backgroundImage: 'url(/img/paper-texture.png)',
                backgroundRepeat: 'repeat',
                borderRadius: 1.5,
                m: 1.5,
                p: [6, 8],
                width: 195,
                height: 120,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center',
                boxShadow: theme?.shadows[2],
                cursor: readOnly ? 'default' : 'pointer',
                pointerEvents: readOnly ? 'none' : undefined,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                textTransform: 'uppercase',
                '&.active:hover': {
                  bgcolor: 'secondary.dark',
                },
                '&.active': {
                  bgcolor: 'secondary.main',
                  color: 'secondary.contrastText',
                  boxShadow: theme.shadows[5],
                },
                '&:not(:hover)': {
                  bgcolor: !currentBuildingUsages.includes(bu.name)
                    ? 'background.default'
                    : undefined,
                },
                '&:hover': {
                  boxShadow: theme.shadows[3],
                  bgcolor: !currentBuildingUsages.includes(bu.name)
                    ? lighten(theme.palette.background.default, 0.25)
                    : undefined,
                },
              }}
              onClick={() => {
                if (readOnly) return;
                if (!subproject) return;
                let updatedUsages: string[];
                if (currentBuildingUsages.includes(bu.name)) {
                  updatedUsages = currentBuildingUsages.filter(
                    (v) => v !== bu.name,
                  );
                } else {
                  updatedUsages = [...currentBuildingUsages, bu.name];
                }
                onUpdate({ ...subproject, buildingUsages: updatedUsages });
              }}
            >
              <Box
                component={'img'}
                src={buildingImageSrc(bu.name)}
              />
              <Typography
                variant={'subtitle2'}
                textTransform={'uppercase'}
                fontWeight={'500'}
                sx={{
                  color: currentBuildingUsages.includes(bu.name)
                    ? 'primary.main' // Green color for selected cards
                    : 'primary.contrastText', // White for unselected
                }}
              >
                {bu.name}
              </Typography>
            </Stack>
          </PaperTooltip>
        ))}
      </Box>
    </React.Fragment>
  );
};
